import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { JOURNAL } from "../lib/content";
import { ShareButton } from "../components/ShareButton";

export default function JournalPostPage() {
  const { slug } = useParams();
  const post = JOURNAL.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/journal" replace />;

  const idx = JOURNAL.findIndex((p) => p.slug === slug);
  const next = JOURNAL[(idx + 1) % JOURNAL.length];

  return (
    <main data-testid="journal-post-page" className="pt-28 pb-24 px-5 md:px-10">
      <article className="max-w-[820px] mx-auto">
        <Link to="/journal" data-testid="journal-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-cream/65 hover:text-ember transition-colors">
          <ArrowLeft size={14} /> The Journal
        </Link>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <div className="flex items-center gap-4 mt-8">
            <span className="font-mono text-[10px] uppercase tracking-widest bg-ember text-void px-2.5 py-1 rounded-full">{post.tag}</span>
            <span className="font-mono text-[11px] text-cream/45">{post.date}</span>
            <span className="font-mono text-[11px] text-cream/45 flex items-center gap-1.5"><Clock size={12} /> {post.read}</span>
          </div>

          <h1 className="font-display font-black uppercase tracking-tight text-cream text-4xl sm:text-5xl leading-[0.95] mt-5" data-testid="journal-post-title">
            {post.title}
          </h1>

          <div className="relative rounded-lg overflow-hidden border border-white/10 mt-10 h-[280px] md:h-[420px]">
            <img src={post.cover} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-void/40 to-transparent" />
          </div>

          <div className="mt-10 space-y-6">
            {post.body.map((para, i) => (
              <p key={`${post.slug}-p${i}`} className="font-mono text-sm md:text-[15px] text-cream/75 leading-[1.9]">
                {para}
              </p>
            ))}
          </div>

          <div className="mt-14 border-t border-white/10 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Link to="/" state={{ scrollTo: "#shop" }} data-testid="journal-post-shop-link" className="font-mono text-xs uppercase tracking-widest bg-ember text-void px-6 py-3 rounded-full font-medium hover:bg-emberDark transition-colors">
                Shop the beans
              </Link>
              <ShareButton kind="journal" slug={post.slug} title={post.title} />
            </div>
            <Link to={`/journal/${next.slug}`} data-testid="journal-next-link" className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-cream/75 hover:text-ember transition-colors text-right">
              Next: {next.title} <ArrowRight size={14} className="shrink-0" />
            </Link>
          </div>
        </motion.div>
      </article>
    </main>
  );
}
